/**
 * Photo Carousel for Gender Party
 * Displays couple photos with swipe navigation
 */

import { useState, useCallback, useEffect, useRef } from "react";
import type { GenderPartyPhoto } from "../hooks";
import styles from "./PhotoCarousel.module.css";

interface PhotoCarouselProps {
  photos: GenderPartyPhoto[];
  couplePhotoUrl?: string | null;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  className?: string;
}

export function PhotoCarousel({
  photos,
  couplePhotoUrl,
  autoPlay = true,
  autoPlayInterval = 5000,
  className = "",
}: PhotoCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartRef = useRef<number | null>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const allPhotos = [
    ...(couplePhotoUrl ? [{ id: "couple", imageUrl: couplePhotoUrl, caption: null, sortOrder: -1 }] : []),
    ...photos.sort((a, b) => a.sortOrder - b.sortOrder),
  ];

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning || allPhotos.length === 0) return;

      setIsTransitioning(true);
      setCurrentIndex((index + allPhotos.length) % allPhotos.length);

      setTimeout(() => setIsTransitioning(false), 500);
    },
    [allPhotos.length, isTransitioning]
  );

  const goToPrev = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  useEffect(() => {
    if (autoPlay && allPhotos.length > 1) {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
    return undefined;
  }, [autoPlay, autoPlayInterval, allPhotos.length, goToNext]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartRef.current === null) return;

      const touchEnd = e.changedTouches[0].clientX;
      const diff = touchStartRef.current - touchEnd;
      const threshold = 50;

      if (diff > threshold) {
        goToNext();
      } else if (diff < -threshold) {
        goToPrev();
      }

      touchStartRef.current = null;

      if (autoPlay && allPhotos.length > 1) {
        autoPlayRef.current = setInterval(() => {
          goToNext();
        }, autoPlayInterval);
      }
    },
    [autoPlay, autoPlayInterval, allPhotos.length, goToNext, goToPrev]
  );

  if (allPhotos.length === 0) {
    return (
      <div className={`${styles.carousel} ${styles.empty} ${className}`}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderIcon}>📷</span>
          <span>Нет фото</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.carousel} ${className}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-label="Карусель фотографий"
      aria-roledescription="carousel"
    >
      <div
        className={styles.track}
        role="group"
        aria-live="polite"
        aria-atomic="false"
      >
        {allPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className={`${styles.slide} ${
              index === currentIndex ? styles.active : ""
            }`}
            style={{
              transform: `translateX(${(index - currentIndex) * 100}%)`,
            }}
            role="tabpanel"
            aria-roledescription="slide"
            aria-label={`Слайд ${index + 1} из ${allPhotos.length}`}
            aria-hidden={index !== currentIndex}
          >
            <img
              src={photo.imageUrl}
              alt={photo.caption ?? `Фото ${index + 1}`}
              className={styles.image}
              loading={index === 0 ? "eager" : "lazy"}
            />
            {photo.caption && (
              <div className={styles.caption}>{photo.caption}</div>
            )}
          </div>
        ))}
      </div>

      {allPhotos.length > 1 && (
        <>
          <button
            type="button"
            className={`${styles.navButton} ${styles.prevButton}`}
            onClick={goToPrev}
            aria-label="Предыдущее фото"
          >
            ‹
          </button>
          <button
            type="button"
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={goToNext}
            aria-label="Следующее фото"
          >
            ›
          </button>

          <div className={styles.indicators} role="tablist" aria-label="Выбор слайда">
            {allPhotos.map((_, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                className={`${styles.indicator} ${
                  index === currentIndex ? styles.activeIndicator : ""
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Перейти к фото ${index + 1}`}
                aria-selected={index === currentIndex}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
